/**
 * Provider API keys at rest.
 *
 * `yada:llm:config` keeps only non-secret settings. The secrets — `openaiKey`,
 * `anthropicKey` and each registered provider's `apiKey` — are stored under
 * `yada:llm:secrets`, encrypted with a non-extractable AES-GCM key that lives in
 * the `yada-llm-secrets` IndexedDB database (standards/key-custody.md).
 *
 * The wrapping key is device-level and holds no user data, so it is kept across
 * logout; the ciphertext is a `yada:` key and is purged with the rest.
 */
import type { LlmConfigV2 } from '@yada/types';

export const LLM_CONFIG_KEY = 'yada:llm:config';
export const LLM_SECRETS_KEY = 'yada:llm:secrets';
export const LLM_SECRETS_DB = 'yada-llm-secrets';

const STORE = 'keys';
const WRAP_KEY_ID = 'wrap';
const FORMAT_VERSION = 1;
// Binds the ciphertext to this purpose and format so it cannot be swapped for
// another AES-GCM blob encrypted under the same key.
const AAD = new TextEncoder().encode(`yada:llm:secrets:v${FORMAT_VERSION}`);

export interface LlmSecrets {
  openaiKey?: string;
  anthropicKey?: string;
  /** Registered provider id → API key */
  providerKeys?: Record<string, string>;
}

interface SecretsEnvelope {
  v: number;
  iv: string;
  ct: string;
}

export function splitLlmSecrets(config: LlmConfigV2): { publicConfig: LlmConfigV2; secrets: LlmSecrets } {
  const { openaiKey, anthropicKey, ...rest } = config;
  const providerKeys: Record<string, string> = {};
  const registeredProviders = config.registeredProviders?.map((provider) => {
    if (provider.apiKey) providerKeys[provider.id] = provider.apiKey;
    return { ...provider, apiKey: '' };
  });
  const secrets: LlmSecrets = {};
  if (openaiKey) secrets.openaiKey = openaiKey;
  if (anthropicKey) secrets.anthropicKey = anthropicKey;
  if (Object.keys(providerKeys).length) secrets.providerKeys = providerKeys;
  return {
    publicConfig: registeredProviders ? { ...rest, registeredProviders } : rest,
    secrets,
  };
}

export function mergeLlmSecrets(config: LlmConfigV2, secrets: LlmSecrets): LlmConfigV2 {
  return {
    ...config,
    ...(secrets.openaiKey ? { openaiKey: secrets.openaiKey } : {}),
    ...(secrets.anthropicKey ? { anthropicKey: secrets.anthropicKey } : {}),
    ...(config.registeredProviders ? {
      registeredProviders: config.registeredProviders.map((provider) => {
        const apiKey = secrets.providerKeys?.[provider.id];
        return apiKey ? { ...provider, apiKey } : provider;
      }),
    } : {}),
  };
}

const hasSecrets = (secrets: LlmSecrets) =>
  Boolean(secrets.openaiKey || secrets.anthropicKey || Object.keys(secrets.providerKeys ?? {}).length);

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(LLM_SECRETS_DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idb<T>(db: IDBDatabase, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = run(db.transaction(STORE, mode).objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function wrappingKey(create: boolean): Promise<CryptoKey | null> {
  const db = await openDb();
  try {
    const existing = await idb<CryptoKey | undefined>(db, 'readonly', (store) => store.get(WRAP_KEY_ID));
    if (existing || !create) return existing ?? null;
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    // `add` rather than `put`: a concurrent tab that created a key first wins, and
    // this tab re-reads it instead of replacing a key that may already encrypt data.
    try {
      await idb(db, 'readwrite', (store) => store.add(key, WRAP_KEY_ID));
      return key;
    } catch {
      return await idb<CryptoKey | undefined>(db, 'readonly', (store) => store.get(WRAP_KEY_ID)) ?? null;
    }
  } finally {
    db.close();
  }
}

const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const fromBase64 = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

async function encryptSecrets(secrets: LlmSecrets): Promise<SecretsEnvelope> {
  const key = await wrappingKey(true);
  if (!key) throw new Error('LLM secrets key unavailable');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(secrets));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: AAD }, key, plaintext));
  return { v: FORMAT_VERSION, iv: toBase64(iv), ct: toBase64(ct) };
}

/** Reads the stored secrets. Throws if they exist but cannot be decrypted. */
export async function loadLlmSecrets(): Promise<LlmSecrets> {
  const raw = localStorage.getItem(LLM_SECRETS_KEY);
  if (!raw) return {};
  const envelope = JSON.parse(raw) as SecretsEnvelope;
  if (envelope.v !== FORMAT_VERSION) throw new Error(`Unknown LLM secrets format v${envelope.v}`);
  const key = await wrappingKey(false);
  if (!key) throw new Error('LLM secrets key missing on this device');
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(envelope.iv), additionalData: AAD },
    key,
    fromBase64(envelope.ct),
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as LlmSecrets;
}

function readPlainConfig(): LlmConfigV2 | null {
  try {
    const raw = localStorage.getItem(LLM_CONFIG_KEY);
    return raw ? JSON.parse(raw) as LlmConfigV2 : null;
  } catch {
    return null;
  }
}

/**
 * Persists a full configuration: secrets encrypted, the rest in plaintext.
 *
 * The secrets are written and read back before the plaintext copy loses them,
 * so a failure at any step leaves the previous storage in place rather than
 * losing the user's keys.
 */
export async function saveLlmConfig(config: LlmConfigV2): Promise<void> {
  const { publicConfig, secrets } = splitLlmSecrets(config);
  if (hasSecrets(secrets)) {
    const envelope = await encryptSecrets(secrets);
    const previous = localStorage.getItem(LLM_SECRETS_KEY);
    localStorage.setItem(LLM_SECRETS_KEY, JSON.stringify(envelope));
    try {
      const readBack = await loadLlmSecrets();
      if (JSON.stringify(readBack) !== JSON.stringify(secrets)) throw new Error('LLM secrets read-back mismatch');
    } catch (error) {
      if (previous === null) localStorage.removeItem(LLM_SECRETS_KEY);
      else localStorage.setItem(LLM_SECRETS_KEY, previous);
      throw error;
    }
  } else {
    localStorage.removeItem(LLM_SECRETS_KEY);
  }
  localStorage.setItem(LLM_CONFIG_KEY, JSON.stringify(publicConfig));
}

/**
 * Moves secrets still held in the plaintext config (written by older builds or
 * by account-settings hydration) into encrypted storage. Plaintext values win
 * over stored ones because they are the most recent write. Leaves everything
 * as it was if encryption is unavailable.
 */
export async function migratePlaintextLlmSecrets(): Promise<void> {
  const plain = readPlainConfig();
  if (!plain || !hasSecrets(splitLlmSecrets(plain).secrets)) return;
  let stored: LlmSecrets = {};
  try {
    stored = await loadLlmSecrets();
  } catch {
    // Unreadable old secrets are replaced only where the plaintext has a value.
  }
  const merged = mergeLlmSecrets(mergeLlmSecrets(plain, stored), splitLlmSecrets(plain).secrets);
  await saveLlmConfig(merged);
}

/** The full configuration, secrets included. Falls back to the plaintext copy. */
export async function loadLlmConfig(): Promise<LlmConfigV2> {
  try {
    await migratePlaintextLlmSecrets();
  } catch {
    // Keys stay in plaintext until encryption works on this device; nothing is lost.
  }
  const plain = readPlainConfig() ?? { provider: null };
  try {
    return mergeLlmSecrets(plain, await loadLlmSecrets());
  } catch {
    return plain;
  }
}
